import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useWorkflowContext } from '../contexts/WorkflowContext';
import type { ContextField } from '../types/workflow-context';

interface VariablePickerProps {
	nodeId: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	className?: string;
	multiline?: boolean;
}

// Update VariablePicker to use forwardRef with memoization
const VariablePicker = React.memo(
	forwardRef<HTMLInputElement | HTMLTextAreaElement, VariablePickerProps>(function VariablePicker(
		{
			nodeId,
			value,
			onChange,
			placeholder = 'Type or select variables...',
			className = '',
			multiline = false,
		},
		ref
	) {
		const [showDropdown, setShowDropdown] = useState(false);
		const [cursorPosition, setCursorPosition] = useState(0);
		const [filteredFields, setFilteredFields] = useState<ContextField[]>([]);
		const [searchTerm, setSearchTerm] = useState('');
		const [visibleCount, setVisibleCount] = useState(20); // Start with 20 items
		const [focusedIndex, setFocusedIndex] = useState(-1);
		const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
		const dropdownRef = useRef<HTMLDivElement>(null);
		const searchInputRef = useRef<HTMLInputElement>(null);

		// Debounced search to prevent excessive filtering
		const debouncedSetSearchTerm = useDebouncedCallback(setSearchTerm, 150);

		useImperativeHandle(ref, () => inputRef.current as any);

		// Get available fields using the hook at the top level
		const { getFlattenedFields } = useWorkflowContext();
		const availableFields = useMemo(() => {
			try {
				const fields = getFlattenedFields(nodeId);

				// Deduplicate fields to prevent duplicate key warnings
				const uniqueFields = Array.from(
					new Map(fields.map(field => [field.id, field])).values()
				);

				// Safety limit to prevent performance issues
				if (uniqueFields.length > 200) {
					console.warn(
						`VariablePicker: Large field count (${uniqueFields.length}). Consider using VariablePickerOptimized for better performance.`
					);
				}

				return uniqueFields;
			} catch (error) {
				console.error('Error getting available fields in VariablePicker:', error);
				return [];
			}
		}, [getFlattenedFields, nodeId]);

		// Handle click outside to close dropdown
		const containerRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
					setShowDropdown(false);
				}
			};

			if (showDropdown) {
				document.addEventListener('mousedown', handleClickOutside);
				return () => {
					document.removeEventListener('mousedown', handleClickOutside);
				};
			}
		}, [showDropdown]);

		// Enhanced fuzzy search with scoring
		const filteredFieldsMemo = useMemo(() => {
			if (!searchTerm.trim()) return availableFields;

			const term = searchTerm.toLowerCase();

			// Score fields based on relevance
			const scoredFields = availableFields
				.map(field => {
					let score = 0;
					const id = field.id.toLowerCase();
					const label = field.label.toLowerCase();
					const description = field.description?.toLowerCase() || '';

					// Exact matches get highest score
					if (id === term) score += 100;
					if (label === term) score += 90;

					// Starts with matches
					if (id.startsWith(term)) score += 50;
					if (label.startsWith(term)) score += 45;

					// Contains matches
					if (id.includes(term)) score += 20;
					if (label.includes(term)) score += 15;
					if (description.includes(term)) score += 5;

					// Word boundary matches (higher relevance)
					const words = term.split(' ');
					words.forEach(word => {
						if (word.length > 1) {
							if (id.includes(word)) score += 10;
							if (label.includes(word)) score += 8;
						}
					});

					return { field, score };
				})
				.filter(item => item.score > 0)
				.sort((a, b) => b.score - a.score)
				.map(item => item.field);

			return scoredFields;
		}, [availableFields, searchTerm]);

		// Update filtered fields when memo changes
		useEffect(() => {
			setFilteredFields(filteredFieldsMemo);
			setVisibleCount(20); // Reset visible count when search changes
			setFocusedIndex(-1); // Reset focus when search changes
		}, [filteredFieldsMemo]);

		// Load more items for infinite scrolling
		const loadMoreItems = useCallback(() => {
			setVisibleCount(prev => Math.min(prev + 20, filteredFields.length));
		}, [filteredFields.length]);

		// Handle scroll to load more
		const handleScroll = useCallback(
			(e: React.UIEvent<HTMLDivElement>) => {
				const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
				const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20;

				if (isNearBottom && visibleCount < filteredFields.length) {
					loadMoreItems();
				}
			},
			[visibleCount, filteredFields.length, loadMoreItems]
		);

		const handleInputChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				const newValue = e.target.value;
				const newCursorPosition = e.target.selectionStart || 0;

				onChange(newValue);
				setCursorPosition(newCursorPosition);

				// Check if user is typing a variable
				const textBeforeCursor = newValue.slice(0, newCursorPosition);
				const lastOpenBrace = textBeforeCursor.lastIndexOf('{{');
				const lastCloseBrace = textBeforeCursor.lastIndexOf('}}');

				if (lastOpenBrace > lastCloseBrace) {
					const partialVariable = textBeforeCursor.slice(lastOpenBrace + 2);
					debouncedSetSearchTerm(partialVariable);
					setShowDropdown(true);
				} else {
					debouncedSetSearchTerm('');
					setShowDropdown(false);
				}
			},
			[onChange, debouncedSetSearchTerm]
		);

		// Handle keyboard navigation in the search input
		const handleSearchKeyDown = useCallback(
			(e: React.KeyboardEvent) => {
				const visibleFields = filteredFields.slice(0, visibleCount);

				switch (e.key) {
					case 'Escape':
						setShowDropdown(false);
						setFocusedIndex(-1);
						setSearchTerm('');
						inputRef.current?.focus();
						break;
					case 'ArrowDown':
						e.preventDefault();
						setFocusedIndex(prev => (prev < visibleFields.length - 1 ? prev + 1 : 0));
						break;
					case 'ArrowUp':
						e.preventDefault();
						setFocusedIndex(prev => (prev > 0 ? prev - 1 : visibleFields.length - 1));
						break;
					case 'Enter':
						e.preventDefault();
						if (focusedIndex >= 0 && focusedIndex < visibleFields.length) {
							insertVariable(visibleFields[focusedIndex]);
						} else if (visibleFields.length > 0) {
							// If no item focused, select the first one
							insertVariable(visibleFields[0]);
						}
						break;
					case 'Tab':
						// Allow tab to close dropdown and return to main input
						setShowDropdown(false);
						setFocusedIndex(-1);
						inputRef.current?.focus();
						break;
				}
			},
			[filteredFields, visibleCount, focusedIndex]
		);

		const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
			if (e.key === 'Escape') {
				setShowDropdown(false);
			}
		}, []);

		const insertVariable = useCallback(
			(field: ContextField) => {
				const textBeforeCursor = value.slice(0, cursorPosition);
				const textAfterCursor = value.slice(cursorPosition);
				const lastOpenBrace = textBeforeCursor.lastIndexOf('{{');
				const lastCloseBrace = textBeforeCursor.lastIndexOf('}}');

				let newValue: string;
				let newCursorPos: number;

				// Check if we're inside an incomplete variable (typing after {{)
				if (lastOpenBrace !== -1 && lastOpenBrace > lastCloseBrace) {
					// Replace the partial variable with the complete one
					const beforeVariable = textBeforeCursor.slice(0, lastOpenBrace);
					newValue = `${beforeVariable}{{${field.id}}}${textAfterCursor}`;
					newCursorPos = beforeVariable.length + field.id.length + 4; // +4 for {{}}
				} else {
					// Insert a new variable at cursor position
					newValue = `${textBeforeCursor}{{${field.id}}}${textAfterCursor}`;
					newCursorPos = textBeforeCursor.length + field.id.length + 4; // +4 for {{}}
				}

				onChange(newValue);
				setShowDropdown(false);
				setFocusedIndex(-1);

				// Focus back to input and set cursor position
				setTimeout(() => {
					inputRef.current?.focus();
					inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
				}, 0);
			},
			[value, cursorPosition, onChange]
		);

		const openVariableMenu = useCallback(() => {
			if (availableFields.length > 0) {
				// Update cursor position to current input position
				const currentCursorPos = inputRef.current?.selectionStart || value.length;
				setCursorPosition(currentCursorPos);
				setFilteredFields(availableFields);
				setShowDropdown(true);
				setSearchTerm(''); // Clear search when opening
				setVisibleCount(20); // Reset visible count
				setFocusedIndex(-1); // Reset focus

				// Focus search input after dropdown opens
				setTimeout(() => {
					searchInputRef.current?.focus();
				}, 0);
			}
		}, [availableFields]);

		// Highlight search terms in text
		const highlightSearchTerm = useCallback((text: string, term: string) => {
			if (!term.trim()) return text;

			const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
			const parts = text.split(regex);

			return parts.map((part, index) =>
				regex.test(part) ? (
					<mark
						key={index}
						className="bg-yellow-200 dark:bg-yellow-800 text-gray-900 dark:text-gray-100 px-0.5 rounded"
					>
						{part}
					</mark>
				) : (
					part
				)
			);
		}, []);

		const InputComponent = multiline ? 'textarea' : 'input';

		return (
			<div className="relative" ref={containerRef}>
				<div className="flex">
					<InputComponent
						ref={inputRef as any}
						type={multiline ? undefined : 'text'}
						value={value}
						onChange={handleInputChange}
						onKeyDown={handleKeyDown}
						placeholder={placeholder}
						className={`flex-1 px-3 py-2 placeholder-gray-500 text-gray-900 bg-white rounded-md border border-gray-300 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 ${className}`}
						rows={multiline ? 4 : undefined}
					/>
					{availableFields.length > 0 && (
						<button
							type="button"
							onClick={openVariableMenu}
							className="px-3 py-1 ml-2 text-sm font-medium text-blue-600 bg-blue-100 rounded transition-colors dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50"
							title="Insert variable"
						>
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
								/>
							</svg>
						</button>
					)}
				</div>

				{showDropdown && (
					<div
						ref={dropdownRef}
						className="overflow-auto absolute z-10 mt-1 w-full max-h-60 bg-white rounded-md border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-600"
						onScroll={handleScroll}
					>
						{/* Search input */}
						<div className="p-3 border-b border-gray-200 dark:border-gray-600">
							<div className="relative">
								<input
									ref={searchInputRef}
									type="text"
									value={searchTerm}
									onChange={e => {
										setSearchTerm(e.target.value);
										setVisibleCount(20); // Reset visible count on search
										setFocusedIndex(-1); // Reset focus on search
									}}
									onKeyDown={handleSearchKeyDown}
									placeholder="Search variables..."
									className="px-3 py-2 pl-8 w-full text-sm bg-gray-50 rounded-lg border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									autoFocus
								/>
								<div className="absolute left-2.5 top-1/2 transform -translate-y-1/2">
									<svg
										className="w-4 h-4 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
										/>
									</svg>
								</div>
								{searchTerm && (
									<button
										onClick={() => {
											setSearchTerm('');
											setVisibleCount(20);
											setFocusedIndex(-1);
											searchInputRef.current?.focus();
										}}
										className="absolute right-2 top-1/2 p-1 text-gray-400 transform -translate-y-1/2 hover:text-gray-600 dark:hover:text-gray-300"
									>
										<svg
											className="w-3 h-3"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									</button>
								)}
							</div>
							<div className="flex justify-between items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
								<span>
									{searchTerm
										? `${filteredFields.length} matches found`
										: `${filteredFields.length} variables available`}
								</span>
								<span className="text-blue-600 dark:text-blue-400">
									↑↓ Navigate • Enter Select • Esc Close
								</span>
							</div>
						</div>

						{/* Variable list with infinite scrolling */}
						{filteredFields.length === 0 ? (
							<div className="p-6 text-center text-gray-500 dark:text-gray-400">
								<div className="mb-2 text-2xl">🔍</div>
								<div className="mb-1 text-sm font-medium">
									{searchTerm
										? `No variables match "${searchTerm}"`
										: 'No variables available'}
								</div>
								<div className="text-xs">
									{searchTerm
										? 'Try a different search term'
										: 'Connect this node to a data source first'}
								</div>
							</div>
						) : (
							filteredFields.slice(0, visibleCount).map((field, index) => (
								<button
									key={`${field.id}-${index}-${field.label}`}
									onClick={() => insertVariable(field)}
									className={`flex flex-col px-3 py-2 w-full text-left border-b border-gray-100 transition-colors dark:border-gray-700 last:border-b-0 ${
										index === focusedIndex
											? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
											: 'hover:bg-gray-50 dark:hover:bg-gray-700'
									}`}
								>
									<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
										{highlightSearchTerm(field.label, searchTerm)}
									</div>
									<div className="font-mono text-xs text-blue-600 dark:text-blue-400">
										{`{{${field.id}}}`}
									</div>
									{field.description && (
										<div className="mt-1 text-xs text-gray-500 truncate dark:text-gray-400">
											{highlightSearchTerm(field.description, searchTerm)}
										</div>
									)}
								</button>
							))
						)}

						{/* Load more indicator */}
						{visibleCount < filteredFields.length && (
							<div className="p-3 text-center border-t border-gray-200 dark:border-gray-600">
								<button
									onClick={loadMoreItems}
									className="text-xs text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
								>
									Load {Math.min(20, filteredFields.length - visibleCount)} more
									variables...
								</button>
								<div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
									Showing {visibleCount} of {filteredFields.length} • Scroll to
									load more
								</div>
							</div>
						)}
					</div>
				)}

				{availableFields.length === 0 && (
					<div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
						No upstream data available. Connect this node to a submission form or other
						data source.
					</div>
				)}
			</div>
		);
	})
);

export { VariablePicker };

// Helper component for simple variable insertion without the full picker UI
interface VariableButtonProps {
	nodeId: string;
	onInsert: (variable: string) => void;
	className?: string;
}

export function VariableButton({ nodeId, onInsert, className = '' }: VariableButtonProps) {
	const [showDropdown, setShowDropdown] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// Get available fields using the hook at the top level
	const { getFlattenedFields } = useWorkflowContext();
	const availableFields = useMemo(() => {
		try {
			const fields = getFlattenedFields(nodeId);

			// Deduplicate fields to prevent duplicate key warnings
			const uniqueFields = Array.from(
				new Map(fields.map(field => [field.id, field])).values()
			);

			return uniqueFields;
		} catch (error) {
			console.error('Error getting available fields in VariableButton:', error);
			return [];
		}
	}, [getFlattenedFields, nodeId]);

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: PointerEvent) => {
			setTimeout(() => {
				if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
					setShowDropdown(false);
				}
			}, 0);
		};
		if (showDropdown) {
			document.addEventListener('pointerdown', handleClickOutside);
			return () => {
				document.removeEventListener('pointerdown', handleClickOutside);
			};
		}
	}, [showDropdown]);

	if (availableFields.length === 0) {
		return null;
	}

	return (
		<div className="relative" ref={containerRef}>
			<button
				type="button"
				onClick={() => setShowDropdown(!showDropdown)}
				className={`px-3 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded transition-colors dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 ${className}`}
			>
				Insert Variable
			</button>

			{showDropdown && (
				<div className="overflow-auto absolute z-10 mt-1 w-64 max-h-60 bg-white rounded-md border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-600">
					<div className="p-2 text-xs text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-600">
						Available variables:
					</div>
					{availableFields.map((field, index) => (
						<button
							key={`${field.id}-${index}-${field.label}`}
							onClick={() => {
								onInsert(`{{${field.id}}}`);
								setShowDropdown(false);
							}}
							className="flex flex-col px-3 py-2 w-full text-left border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 last:border-b-0"
						>
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{field.label}
							</div>
							<div className="font-mono text-xs text-gray-500 dark:text-gray-400">
								{`{{${field.id}}}`}
							</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
